import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * BackButton — discrete "← Retour" control for public pages.
 *
 * Behaviour:
 *   - If there is a real in-app history entry, ``navigate(-1)`` returns
 *     to the previous view. We treat ``window.history.length > 1`` as a
 *     reliable signal that the user reached this page from another page
 *     inside My Fleet.
 *   - Otherwise (direct URL access, hard reload, external referrer) we
 *     redirect to the app root ``/`` so the user is never trapped on a
 *     public page.
 *
 * Accessibility:
 *   - ``type="button"`` so it never accidentally submits a form.
 *   - ``aria-label`` matches the mission spec.
 *   - Large enough hit area (≥ 40px height) for iPhone touch targets.
 *   - Visible focus outline for keyboard users.
 */
export function BackButton({ testId = "back-button" }) {
  const navigate = useNavigate();

  const onClick = () => {
    // "Real" in-app history requires BOTH:
    //   - the browser stack contains more than one entry, AND
    //   - the referrer either points to our own origin, OR the current
    //     history state carries a React Router marker (``key`` field),
    //     which is set when the user reached this page via ``<Link>``
    //     or ``navigate()`` from another page of the SPA.
    // When neither condition holds (direct URL access, hard reload,
    // external referrer, TWA/Capacitor deep-link, etc.) we fall back
    // to ``/`` so the user is never trapped.
    let inAppHistory = false;
    if (typeof window !== "undefined") {
      try {
        const hasHistory = !!(window.history && window.history.length > 1);
        const routerKey = window.history?.state?.key || null;
        const sameOriginRef =
          typeof document !== "undefined" &&
          document.referrer &&
          document.referrer.startsWith(window.location.origin);
        inAppHistory = hasHistory && (routerKey || sameOriginRef);
      } catch { /* ignore */ }
    }
    if (inAppHistory) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label="Revenir à la page précédente"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 12px",
        marginBottom: 4,
        background: "transparent",
        border: "1px solid var(--mf-divider)",
        borderRadius: 999,
        color: "var(--mf-text)",
        fontSize: 14,
        fontWeight: 500,
        cursor: "pointer",
        lineHeight: 1,
        minHeight: 36,
      }}
      onFocus={(e) => { e.currentTarget.style.outline = "2px solid var(--mf-brand)"; e.currentTarget.style.outlineOffset = "2px"; }}
      onBlur={(e) => { e.currentTarget.style.outline = "none"; }}
    >
      <ArrowLeft size={16} aria-hidden="true" />
      <span>Retour</span>
    </button>
  );
}

export default BackButton;
